import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button, Card, Input, useToast } from '@caruso/ui';
import {
  calendarService,
  CalendarEvent,
  CalendarEventType,
  CalendarEventStatus,
  CreateCalendarEventDto,
  UpdateCalendarEventDto,
  AppleCalendarCredentials,
} from '@/services/calendar.service';
import { clientsService } from '@/services/clients.service';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Plus,
  Users,
  X,
  Trash2,
  RefreshCw,
  Link2,
  Link2Off,
  Settings,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Key,
  Shield,
  ArrowRight,
  Eye,
  EyeOff,
} from 'lucide-react';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  formatDistanceToNow,
  addHours,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';

export function CalendarPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [userCalendarId, setUserCalendarId] = useState<string | null>(null);
  const [showSyncPanel, setShowSyncPanel] = useState(false);
  const [showAppleModal, setShowAppleModal] = useState(false);
  const [appleWizardStep, setAppleWizardStep] = useState<'tutorial' | 'form'>('tutorial');
  const [showApplePassword, setShowApplePassword] = useState(false);
  const [appleCredentials, setAppleCredentials] = useState<AppleCalendarCredentials>({
    calendarUrl: 'https://caldav.icloud.com/',
    username: '',
    password: '',
  });

  const [formData, setFormData] = useState<CreateCalendarEventDto>({
    calendarId: '',
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    type: CalendarEventType.MEETING,
    status: CalendarEventStatus.SCHEDULED,
    allDay: false,
  });

  // Estado para busca e seleção de múltiplos clientes
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClients, setSelectedClients] = useState<Array<{ id: string; name: string; email?: string }>>([]);
  const [sendInviteEmail, setSendInviteEmail] = useState(false);

  // Buscar status de sincronização
  const { data: syncStatus, refetch: refetchSyncStatus } = useQuery({
    queryKey: ['calendar-sync-status'],
    queryFn: () => calendarService.getSyncStatus(),
  });

  // Polling automático a cada 3 minutos quando Apple Calendar está conectado
  useEffect(() => {
    if (!syncStatus?.apple.connected) return;

    const pollingInterval = setInterval(() => {
      // Sincroniza silenciosamente (sem toast)
      calendarService.manualSync().then(() => {
        queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
        refetchSyncStatus();
      }).catch((error) => {
        console.warn('Auto-sync failed:', error);
      });
    }, 3 * 60 * 1000); // 3 minutos

    return () => clearInterval(pollingInterval);
  }, [syncStatus?.apple.connected, queryClient, refetchSyncStatus]);

  // Tratar parâmetros da URL (callback do OAuth)
  useEffect(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');

    if (success === 'google_connected') {
      toast({
        title: 'Google Calendar conectado!',
        description: 'Sua agenda foi sincronizada com o Google Calendar.',
        variant: 'success',
      });
      refetchSyncStatus();
      setSearchParams({});
    } else if (error) {
      const errorMessages: Record<string, string> = {
        google_oauth_denied: 'Você negou a permissão para conectar o Google Calendar.',
        google_oauth_failed: 'Erro ao conectar com o Google Calendar. Tente novamente.',
        missing_params: 'Parâmetros inválidos na URL de callback.',
      };
      toast({
        title: 'Erro na conexão',
        description: errorMessages[error] || 'Erro desconhecido.',
        variant: 'destructive',
      });
      setSearchParams({});
    }
  }, [searchParams]);

  // Mutation para conectar Google
  const connectGoogleMutation = useMutation({
    mutationFn: () => calendarService.getGoogleAuthUrl(),
    onSuccess: (data) => {
      window.location.href = data.authUrl;
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao conectar',
        description:
          error.response?.data?.message || 'Não foi possível iniciar a conexão com o Google.',
        variant: 'destructive',
      });
    },
  });

  // Mutation para desconectar Google
  const disconnectGoogleMutation = useMutation({
    mutationFn: () => calendarService.disconnectGoogle(),
    onSuccess: () => {
      toast({
        title: 'Google Calendar desconectado',
        description: 'A conexão com o Google Calendar foi removida.',
        variant: 'success',
      });
      refetchSyncStatus();
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao desconectar',
        description: error.response?.data?.message || 'Erro desconhecido.',
        variant: 'destructive',
      });
    },
  });

  // Mutation para conectar Apple
  const connectAppleMutation = useMutation({
    mutationFn: (data: AppleCalendarCredentials) => calendarService.connectAppleCalendar(data),
    onSuccess: () => {
      toast({
        title: 'Apple Calendar conectado!',
        description: 'Sua agenda foi sincronizada com o Apple Calendar.',
        variant: 'success',
      });
      setShowAppleModal(false);
      setAppleCredentials({
        calendarUrl: 'https://caldav.icloud.com/',
        username: '',
        password: '',
      });
      refetchSyncStatus();
    },
    onError: (error: any) => {
      console.log('Apple Calendar connection error:', error);
      console.log('Error response:', error.response);
      const errorMessage = error.response?.data?.message || 'Verifique suas credenciais e tente novamente.';
      console.log('Error message to display:', errorMessage);
      toast({
        title: 'Erro ao conectar Apple Calendar',
        description: errorMessage,
        variant: 'destructive',
      });
      // Mantém no form para o usuário corrigir
      setAppleWizardStep('form');
    },
  });

  // Mutation para desconectar Apple
  const disconnectAppleMutation = useMutation({
    mutationFn: () => calendarService.disconnectApple(),
    onSuccess: () => {
      toast({
        title: 'Apple Calendar desconectado',
        description: 'A conexão com o Apple Calendar foi removida.',
        variant: 'success',
      });
      refetchSyncStatus();
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao desconectar',
        description: error.response?.data?.message || 'Erro desconhecido.',
        variant: 'destructive',
      });
    },
  });

  // Mutation para sincronização manual
  const manualSyncMutation = useMutation({
    mutationFn: () => calendarService.manualSync(),
    onSuccess: (result) => {
      const messages: string[] = [];
      if (result.google.synced) messages.push('Google');
      if (result.apple.synced) messages.push('Apple');

      if (messages.length > 0) {
        toast({
          title: 'Sincronização concluída',
          description: `Sincronizado com: ${messages.join(', ')}`,
          variant: 'success',
        });
      } else {
        toast({
          title: 'Nenhuma sincronização',
          description: 'Nenhum calendário externo conectado.',
          variant: 'default',
        });
      }
      refetchSyncStatus();
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro na sincronização',
        description: error.response?.data?.message || 'Erro desconhecido.',
        variant: 'destructive',
      });
    },
  });

  // Buscar calendários do usuário
  const { data: calendars, isLoading: isLoadingCalendars } = useQuery({
    queryKey: ['calendars'],
    queryFn: () => calendarService.getCalendars(),
  });

  // Mutation para habilitar/criar calendário automaticamente
  const enableCalendarMutation = useMutation({
    mutationFn: () => calendarService.enableCalendar(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendars'] });
      toast({
        title: 'Agenda habilitada',
        description: 'Sua agenda foi criada com sucesso',
        variant: 'success',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao habilitar agenda',
        description: error.response?.data?.message || 'Erro desconhecido',
        variant: 'destructive',
      });
    },
  });

  // Criar calendário automaticamente se não existir
  useEffect(() => {
    if (
      !isLoadingCalendars &&
      calendars &&
      calendars.length === 0 &&
      !enableCalendarMutation.isPending
    ) {
      enableCalendarMutation.mutate();
    }
  }, [calendars, isLoadingCalendars]);

  // Definir o calendário padrão
  useEffect(() => {
    if (calendars && calendars.length > 0 && !userCalendarId) {
      setUserCalendarId(calendars[0].id);
    }
  }, [calendars, userCalendarId]);

  // Buscar eventos do mês atual
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);

  const { data: eventsResponse, isLoading } = useQuery({
    queryKey: ['calendar-events', userCalendarId, monthStart, monthEnd],
    queryFn: () =>
      calendarService.getEvents({
        calendarId: userCalendarId || undefined,
        startDate: monthStart.toISOString(),
        endDate: monthEnd.toISOString(),
        limit: 1000,
      }),
    enabled: !!userCalendarId,
  });

  const events = eventsResponse?.data || [];

  // Buscar clientes para associar a reuniões (com busca)
  const { data: clientsResponse } = useQuery({
    queryKey: ['clients-search', clientSearch],
    queryFn: () => clientsService.getAll({ search: clientSearch, page: 1, pageSize: 10 }),
    enabled: clientSearch.length > 2,
  });
  const searchedClients = clientsResponse?.data || [];

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateCalendarEventDto) => calendarService.createEvent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      toast({
        title: 'Evento criado',
        description: 'O evento foi criado com sucesso',
        variant: 'success',
      });
      handleCloseModal();
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao criar evento',
        description: error.response?.data?.message || 'Erro desconhecido',
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCalendarEventDto }) =>
      calendarService.updateEvent(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      toast({
        title: 'Evento atualizado',
        description: 'O evento foi atualizado com sucesso',
        variant: 'success',
      });
      handleCloseModal();
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao atualizar evento',
        description: error.response?.data?.message || 'Erro desconhecido',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => calendarService.deleteEvent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      toast({
        title: 'Evento excluído',
        description: 'O evento foi excluído com sucesso',
        variant: 'success',
      });
      handleCloseModal();
    },
  });

  // Gerar dias do calendário
  const monthStartDate = startOfMonth(currentDate);
  const monthEndDate = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStartDate, { locale: ptBR });
  const calendarEnd = endOfWeek(monthEndDate, { locale: ptBR });
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const handlePreviousMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const handleDayClick = (day: Date) => {
    setSelectedDate(day);
    setFormData({
      ...formData,
      calendarId: userCalendarId || '',
      startDate: format(day, "yyyy-MM-dd'T'09:00"),
      endDate: format(day, "yyyy-MM-dd'T'10:00"),
    });
    setShowEventModal(true);
  };

  const handleEditEvent = async (event: CalendarEvent) => {
    setEditingEvent(event);
    // Formatar datas para o formato do input datetime-local (YYYY-MM-DDTHH:mm)
    const formatDateForInput = (dateStr: string) => {
      const date = new Date(dateStr);
      return format(date, "yyyy-MM-dd'T'HH:mm");
    };
    setFormData({
      calendarId: event.calendarId,
      title: event.title,
      description: event.description,
      location: event.location,
      type: event.type,
      status: event.status,
      startDate: formatDateForInput(event.startDate),
      endDate: formatDateForInput(event.endDate),
      allDay: event.allDay,
      clientId: event.clientId,
    });

    // Carregar clientes associados ao evento (backward compatibility com clientId único)
    const clientsToLoad: Array<{ id: string; name: string; email?: string }> = [];
    if (event.clientId) {
      try {
        const client = await clientsService.getById(event.clientId);
        clientsToLoad.push({ id: client.id, name: client.name, email: client.email || undefined });
      } catch {
        // Cliente não encontrado, ignora
      }
    }
    setSelectedClients(clientsToLoad);
    setSendInviteEmail(false);

    setShowEventModal(true);
  };

  const handleDeleteEvent = (id: string, title: string) => {
    if (confirm(`Tem certeza que deseja excluir o evento "${title}"?`)) {
      deleteMutation.mutate(id);
    }
  };

  const handleCloseModal = () => {
    setShowEventModal(false);
    setEditingEvent(null);
    setSelectedDate(null);
    setClientSearch('');
    setSelectedClients([]);
    setSendInviteEmail(false);
    setFormData({
      calendarId: userCalendarId || '',
      title: '',
      description: '',
      startDate: '',
      endDate: '',
      type: CalendarEventType.MEETING,
      status: CalendarEventStatus.SCHEDULED,
      allDay: false,
      clientId: undefined,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Verificar se algum cliente tem email para o envio de convite
    const clientsWithEmail = selectedClients.filter((c) => c.email);
    const clientIds = selectedClients.map((c) => c.id);

    const payload = {
      ...formData,
      clientIds: clientIds.length > 0 ? clientIds : undefined,
      clientId: clientIds.length > 0 ? clientIds[0] : undefined, // backward compatibility
      sendInviteEmail: sendInviteEmail && clientsWithEmail.length > 0,
    };

    if (editingEvent) {
      updateMutation.mutate({ id: editingEvent.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const getEventsForDay = (day: Date) => {
    return events.filter((event) => {
      const eventStart = new Date(event.startDate);
      return isSameDay(eventStart, day);
    });
  };

  const getEventTypeColor = (type: CalendarEventType) => {
    const colors = {
      [CalendarEventType.MEETING]: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      [CalendarEventType.APPOINTMENT]: 'bg-green-500/20 text-green-400 border-green-500/30',
      [CalendarEventType.TASK]: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      [CalendarEventType.REMINDER]: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      [CalendarEventType.BREAK]: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      [CalendarEventType.OTHER]: 'bg-brand-gold/20 text-brand-gold border-brand-gold/30',
    };
    return colors[type] || colors[CalendarEventType.OTHER];
  };

  // Mostrar loading enquanto carrega ou cria calendário
  if (
    isLoadingCalendars ||
    enableCalendarMutation.isPending ||
    !calendars ||
    calendars.length === 0
  ) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <CalendarIcon className="w-16 h-16 text-brand-gold/20 mb-4 animate-pulse" />
        <h2 className="text-2xl font-serif text-brand-gold mb-2">
          {enableCalendarMutation.isPending ? 'Configurando sua agenda...' : 'Carregando agenda...'}
        </h2>
        <p className="text-brand-ivory/60 text-center max-w-md">
          Aguarde um momento enquanto preparamos sua agenda.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-serif text-brand-gold">Minha Agenda</h1>
          <p className="text-brand-ivory/60 mt-1">Gerencie seus compromissos e eventos</p>
        </div>
        <div className="flex gap-2">
          {/* Botão Sincronizar Agora - apenas para Apple Calendar (não tem webhook) */}
          {syncStatus?.apple.connected && (
            <Button
              variant="outline"
              onClick={() => manualSyncMutation.mutate()}
              disabled={manualSyncMutation.isPending}
              className="border-brand-gold/30 text-brand-gold"
              title="Sincronizar com Apple Calendar"
            >
              <RefreshCw
                className={`w-4 h-4 mr-2 ${manualSyncMutation.isPending ? 'animate-spin' : ''}`}
              />
              {manualSyncMutation.isPending ? 'Sincronizando...' : 'Sincronizar'}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setShowSyncPanel(!showSyncPanel)}
            className={`border-brand-gold/30 ${
              syncStatus?.google.connected || syncStatus?.apple.connected
                ? 'text-green-400 border-green-500/30'
                : 'text-brand-gold'
            }`}
          >
            <Link2 className="w-4 h-4 mr-2" />
            {syncStatus?.google.connected || syncStatus?.apple.connected
              ? 'Calendários Conectados'
              : 'Conectar Calendário'}
          </Button>
          <Button onClick={() => handleDayClick(new Date())} variant="premium">
            <Plus className="w-4 h-4 mr-2" />
            Novo Evento
          </Button>
        </div>
      </div>

      {/* Sync Panel */}
      {showSyncPanel && (
        <Card className="bg-brand-ink/50 border-brand-gold/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-brand-gold/10">
                <Link2 className="w-5 h-5 text-brand-gold" />
              </div>
              <div>
                <h3 className="text-lg font-serif text-brand-gold">Integrações de Calendário</h3>
                <p className="text-sm text-brand-ivory/60">
                  Sincronize sua agenda com Google Calendar ou Apple Calendar
                </p>
              </div>
            </div>
            {(syncStatus?.google.connected || syncStatus?.apple.connected) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => manualSyncMutation.mutate()}
                disabled={manualSyncMutation.isPending}
                className="border-brand-gold/30 text-brand-gold"
              >
                <RefreshCw
                  className={`w-4 h-4 mr-2 ${manualSyncMutation.isPending ? 'animate-spin' : ''}`}
                />
                Sincronizar Agora
              </Button>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Google Calendar */}
            <div
              className={`p-4 rounded-lg border ${
                syncStatus?.google.connected
                  ? 'bg-green-500/10 border-green-500/30'
                  : 'bg-brand-smoke/30 border-brand-gold/20'
              }`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-6 h-6">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-brand-ivory">Google Calendar</h4>
                  {syncStatus?.google.connected ? (
                    <div className="flex items-center gap-1 text-sm text-green-400">
                      <CheckCircle className="w-3 h-3" />
                      Conectado
                    </div>
                  ) : (
                    <p className="text-sm text-brand-ivory/50">Não conectado</p>
                  )}
                </div>
              </div>

              {syncStatus?.google.connected ? (
                <div className="space-y-3">
                  {syncStatus.google.lastSync && (
                    <p className="text-xs text-brand-ivory/50">
                      Última sincronização:{' '}
                      {formatDistanceToNow(new Date(syncStatus.google.lastSync), {
                        addSuffix: true,
                        locale: ptBR,
                      })}
                    </p>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (confirm('Tem certeza que deseja desconectar o Google Calendar?')) {
                        disconnectGoogleMutation.mutate();
                      }
                    }}
                    disabled={disconnectGoogleMutation.isPending}
                    className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
                  >
                    <Link2Off className="w-4 h-4 mr-2" />
                    Desconectar
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => connectGoogleMutation.mutate()}
                  disabled={connectGoogleMutation.isPending}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {connectGoogleMutation.isPending ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ExternalLink className="w-4 h-4 mr-2" />
                  )}
                  Conectar Google Calendar
                </Button>
              )}
            </div>

            {/* Apple Calendar */}
            <div
              className={`p-4 rounded-lg border ${
                syncStatus?.apple.connected
                  ? 'bg-green-500/10 border-green-500/30'
                  : 'bg-brand-smoke/30 border-brand-gold/20'
              }`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center">
                  <CalendarIcon className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-brand-ivory">Apple Calendar</h4>
                  {syncStatus?.apple.connected ? (
                    <div className="flex items-center gap-1 text-sm text-green-400">
                      <CheckCircle className="w-3 h-3" />
                      Conectado ({syncStatus.apple.username})
                    </div>
                  ) : (
                    <p className="text-sm text-brand-ivory/50">Não conectado</p>
                  )}
                </div>
              </div>

              {syncStatus?.apple.connected ? (
                <div className="space-y-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (confirm('Tem certeza que deseja desconectar o Apple Calendar?')) {
                        disconnectAppleMutation.mutate();
                      }
                    }}
                    disabled={disconnectAppleMutation.isPending}
                    className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
                  >
                    <Link2Off className="w-4 h-4 mr-2" />
                    Desconectar
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => setShowAppleModal(true)}
                  className="w-full bg-gray-700 hover:bg-gray-600 text-white"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Configurar Apple Calendar
                </Button>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="mt-4 p-3 rounded-lg bg-brand-gold/10 border border-brand-gold/20">
            <p className="text-sm text-brand-ivory/70">
              <strong className="text-brand-gold">Sincronização bidirecional:</strong> Eventos
              criados aqui serão enviados para seu calendário externo, e eventos do calendário
              externo serão importados automaticamente.
            </p>
          </div>
        </Card>
      )}

      {/* Calendar Navigation */}
      <Card className="bg-brand-ink/50 border-brand-gold/20">
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-serif text-brand-gold capitalize">
              {format(currentDate, 'MMMM yyyy', { locale: ptBR })}
            </h2>
            <div className="flex gap-2">
              <Button variant="outline" size="icon" onClick={handlePreviousMonth}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={handleNextMonth}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-2">
            {/* Week days header */}
            {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((day) => (
              <div key={day} className="text-center text-sm font-medium text-brand-ivory/60 pb-2">
                {day}
              </div>
            ))}

            {/* Calendar days */}
            {calendarDays.map((day, index) => {
              const dayEvents = getEventsForDay(day);
              const isCurrentMonth = isSameMonth(day, currentDate);
              const isToday = isSameDay(day, new Date());

              return (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.02 }}
                  className={`
                    min-h-24 p-2 rounded-lg border cursor-pointer transition-all
                    ${isCurrentMonth ? 'bg-brand-smoke/30 border-brand-gold/20' : 'bg-brand-smoke/10 border-brand-gold/10'}
                    ${isToday ? 'ring-2 ring-brand-gold' : ''}
                    hover:border-brand-gold/40
                  `}
                  onClick={() => handleDayClick(day)}
                >
                  <div
                    className={`text-sm font-medium mb-1 ${
                      isCurrentMonth ? 'text-brand-ivory' : 'text-brand-ivory/30'
                    } ${isToday ? 'text-brand-gold' : ''}`}
                  >
                    {format(day, 'd')}
                  </div>
                  <div className="space-y-1">
                    {dayEvents.slice(0, 2).map((event) => {
                      const eventTime = format(new Date(event.startDate), 'HH:mm');
                      const displayText = event.allDay
                        ? event.title
                        : `${eventTime} - ${event.title}`;
                      return (
                        <div
                          key={event.id}
                          className={`text-xs px-1 py-0.5 rounded border truncate cursor-pointer hover:opacity-80 ${getEventTypeColor(event.type)}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditEvent(event);
                          }}
                          title={displayText}
                        >
                          {displayText}
                        </div>
                      );
                    })}
                    {dayEvents.length > 2 && (
                      <div className="text-xs text-brand-ivory/50">
                        +{dayEvents.length - 2} mais
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Event Modal */}
      {showEventModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-brand-ink border border-brand-gold/20 rounded-lg p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-serif text-brand-gold">
                {editingEvent ? 'Editar Evento' : 'Novo Evento'}
              </h2>
              <Button variant="ghost" size="icon" onClick={handleCloseModal}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-brand-ivory mb-2">Título</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Nome do evento"
                  required
                  className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-ivory mb-2">Descrição</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Detalhes do evento"
                  rows={3}
                  className="w-full px-3 py-2 bg-brand-smoke/50 border border-brand-gold/20 rounded-lg text-brand-ivory focus:outline-none focus:ring-2 focus:ring-brand-gold/50"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-brand-ivory mb-2">
                    Data/Hora Início
                  </label>
                  <Input
                    type="datetime-local"
                    value={formData.startDate}
                    onChange={(e) => {
                      const newStartDate = e.target.value;
                      // Auto-atualiza endDate para ser startDate + 1 hora
                      const startDateObj = new Date(newStartDate);
                      const endDateObj = addHours(startDateObj, 1);
                      const newEndDate = format(endDateObj, "yyyy-MM-dd'T'HH:mm");
                      setFormData({ ...formData, startDate: newStartDate, endDate: newEndDate });
                    }}
                    required
                    className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-ivory mb-2">
                    Data/Hora Fim
                  </label>
                  <Input
                    type="datetime-local"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    required
                    className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-ivory mb-2">Tipo</label>
                <select
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      type: e.target.value as CalendarEventType,
                      // Limpa clientId se não for reunião
                      clientId: e.target.value === CalendarEventType.MEETING ? formData.clientId : undefined,
                    })
                  }
                  className="w-full px-3 py-2 bg-brand-smoke/50 border border-brand-gold/20 rounded-lg text-brand-ivory focus:outline-none focus:ring-2 focus:ring-brand-gold/50"
                >
                  <option value={CalendarEventType.MEETING}>Reunião</option>
                  <option value={CalendarEventType.APPOINTMENT}>Compromisso</option>
                  <option value={CalendarEventType.TASK}>Tarefa</option>
                  <option value={CalendarEventType.REMINDER}>Lembrete</option>
                  <option value={CalendarEventType.BREAK}>Intervalo</option>
                  <option value={CalendarEventType.OTHER}>Outro</option>
                </select>
              </div>

              {/* Campo de clientes - aparece apenas para reuniões */}
              {formData.type === CalendarEventType.MEETING && (
                <div className="relative">
                  <label className="block text-sm font-medium text-brand-ivory mb-2">
                    <Users className="w-4 h-4 inline mr-1" />
                    Clientes Associados
                  </label>

                  {/* Lista de clientes selecionados */}
                  {selectedClients.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {selectedClients.map((client) => (
                        <div
                          key={client.id}
                          className="flex items-center gap-1 px-2 py-1 bg-brand-gold/10 border border-brand-gold/30 rounded-lg"
                        >
                          <span className="text-sm text-brand-ivory">{client.name}</span>
                          {client.email && (
                            <span className="text-xs text-brand-ivory/50">({client.email})</span>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedClients(selectedClients.filter((c) => c.id !== client.id));
                              if (selectedClients.length === 1) {
                                setSendInviteEmail(false);
                              }
                            }}
                            className="p-0.5 hover:bg-brand-gold/20 rounded transition-colors ml-1"
                          >
                            <X className="w-3 h-3 text-brand-ivory/70" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Campo de busca para adicionar mais clientes */}
                  <Input
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    placeholder="Digite para buscar e adicionar clientes (mín. 3 caracteres)..."
                    className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                  />

                  {/* Dropdown de resultados */}
                  {searchedClients.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-brand-ink border border-brand-gold/20 rounded-lg shadow-lg max-h-60 overflow-auto">
                      {searchedClients
                        .filter((client) => !selectedClients.find((c) => c.id === client.id))
                        .map((client) => (
                          <div
                            key={client.id}
                            onClick={() => {
                              setSelectedClients([
                                ...selectedClients,
                                { id: client.id, name: client.name, email: client.email || undefined },
                              ]);
                              setClientSearch('');
                            }}
                            className="p-3 hover:bg-brand-gold/10 cursor-pointer border-b border-brand-gold/10 last:border-0"
                          >
                            <div className="font-medium text-brand-ivory">{client.name}</div>
                            <div className="text-sm text-brand-ivory/60">
                              {client.email || client.phone || 'Sem contato'}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}

                  <p className="text-xs text-brand-ivory/50 mt-1">
                    Os nomes dos clientes serão incluídos nas notas ao sincronizar
                  </p>

                  {/* Checkbox para enviar convite por email - aparece se algum cliente tem email */}
                  {selectedClients.some((c) => c.email) && (
                    <label className="flex items-center gap-2 mt-3 p-2 bg-blue-500/10 border border-blue-500/20 rounded-lg cursor-pointer hover:bg-blue-500/15 transition-colors">
                      <input
                        type="checkbox"
                        checked={sendInviteEmail}
                        onChange={(e) => setSendInviteEmail(e.target.checked)}
                        className="w-4 h-4 rounded border-brand-gold/30 text-blue-500 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <span className="text-sm text-brand-ivory">Enviar convite por email</span>
                        <p className="text-xs text-brand-ivory/50">
                          Será enviado um arquivo com o agendamento (.ics) para o email dos participantes
                        </p>
                      </div>
                    </label>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-brand-ivory mb-2">Local</label>
                <Input
                  value={formData.location || ''}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Local do evento"
                  className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                />
              </div>

              <div className="flex gap-3 pt-4">
                {editingEvent && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleDeleteEvent(editingEvent.id, editingEvent.title)}
                    disabled={deleteMutation.isPending}
                    className="text-red-400 hover:text-red-300 disabled:opacity-50"
                  >
                    {deleteMutation.isPending ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-2" />
                    )}
                    {deleteMutation.isPending ? 'Excluindo...' : 'Excluir'}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseModal}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="flex-1"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Salvando...'
                    : editingEvent
                      ? 'Atualizar'
                      : 'Criar Evento'}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Apple Calendar Modal - Wizard com Tutorial */}
      {showAppleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-brand-ink border border-brand-gold/20 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gray-600 to-gray-700 flex items-center justify-center">
                  <CalendarIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-serif text-brand-gold">Conectar Apple Calendar</h2>
                  <p className="text-xs text-brand-ivory/50">
                    {appleWizardStep === 'tutorial'
                      ? 'Passo 1 de 2: Tutorial'
                      : 'Passo 2 de 2: Configurar'}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setShowAppleModal(false);
                  setAppleWizardStep('tutorial');
                }}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Step Indicator */}
            <div className="flex items-center gap-2 mb-6">
              <div
                className={`flex-1 h-1 rounded-full ${appleWizardStep === 'tutorial' ? 'bg-brand-gold' : 'bg-brand-gold'}`}
              />
              <div
                className={`flex-1 h-1 rounded-full ${appleWizardStep === 'form' ? 'bg-brand-gold' : 'bg-brand-smoke/50'}`}
              />
            </div>

            {appleWizardStep === 'tutorial' ? (
              /* Tutorial Step */
              <div className="space-y-6">
                {/* Explicação */}
                <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                  <div className="flex gap-3">
                    <Shield className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-medium text-amber-500 mb-1">
                        Por que preciso de uma senha de app?
                      </h3>
                      <p className="text-sm text-brand-ivory/70">
                        A Apple exige uma{' '}
                        <strong className="text-brand-ivory">
                          senha específica para aplicativos
                        </strong>{' '}
                        quando você tem a autenticação de dois fatores ativada. Isso protege sua
                        conta permitindo que apps externos acessem apenas o calendário, sem acesso
                        total à sua conta.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Passo a Passo */}
                <div>
                  <h3 className="text-lg font-medium text-brand-gold mb-4 flex items-center gap-2">
                    <Key className="w-5 h-5" />
                    Como criar sua Senha de App
                  </h3>

                  <div className="space-y-4">
                    {/* Passo 1 */}
                    <div className="flex gap-4 p-4 rounded-lg bg-brand-smoke/30 border border-brand-gold/10">
                      <div className="w-8 h-8 rounded-full bg-brand-gold text-brand-ink flex items-center justify-center font-bold flex-shrink-0">
                        1
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-brand-ivory mb-1">
                          Acesse sua conta Apple ID
                        </h4>
                        <p className="text-sm text-brand-ivory/70 mb-3">
                          Clique no botão abaixo para abrir a página de gerenciamento da sua conta
                          Apple.
                        </p>
                        <a
                          href="https://appleid.apple.com/account/manage"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm transition-colors"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Abrir Apple ID
                        </a>
                      </div>
                    </div>

                    {/* Passo 2 */}
                    <div className="flex gap-4 p-4 rounded-lg bg-brand-smoke/30 border border-brand-gold/10">
                      <div className="w-8 h-8 rounded-full bg-brand-gold text-brand-ink flex items-center justify-center font-bold flex-shrink-0">
                        2
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-brand-ivory mb-1">
                          Faça login e vá em "Início de sessão e segurança"
                        </h4>
                        <p className="text-sm text-brand-ivory/70">
                          Após fazer login, procure a seção{' '}
                          <strong className="text-brand-ivory">
                            "Início de sessão e segurança"
                          </strong>{' '}
                          no menu lateral.
                        </p>
                      </div>
                    </div>

                    {/* Passo 3 */}
                    <div className="flex gap-4 p-4 rounded-lg bg-brand-smoke/30 border border-brand-gold/10">
                      <div className="w-8 h-8 rounded-full bg-brand-gold text-brand-ink flex items-center justify-center font-bold flex-shrink-0">
                        3
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-brand-ivory mb-1">
                          Clique em "Senhas específicas de apps"
                        </h4>
                        <p className="text-sm text-brand-ivory/70">
                          Role para baixo até encontrar{' '}
                          <strong className="text-brand-ivory">"Senhas específicas de apps"</strong>{' '}
                          e clique nela. Se não aparecer, você precisa ativar a autenticação de dois
                          fatores primeiro.
                        </p>
                      </div>
                    </div>

                    {/* Passo 4 */}
                    <div className="flex gap-4 p-4 rounded-lg bg-brand-smoke/30 border border-brand-gold/10">
                      <div className="w-8 h-8 rounded-full bg-brand-gold text-brand-ink flex items-center justify-center font-bold flex-shrink-0">
                        4
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-brand-ivory mb-1">Gere uma nova senha</h4>
                        <p className="text-sm text-brand-ivory/70 mb-2">
                          Clique em{' '}
                          <strong className="text-brand-ivory">
                            "Gerar uma senha específica de app"
                          </strong>
                          , digite um nome como{' '}
                          <strong className="text-brand-ivory">"Caruso CRM"</strong> e clique em
                          "Criar".
                        </p>
                        <div className="p-3 rounded bg-brand-ink border border-brand-gold/20">
                          <p className="text-xs text-brand-ivory/50 mb-1">
                            Exemplo de senha gerada:
                          </p>
                          <code className="text-brand-gold font-mono">xxxx-xxxx-xxxx-xxxx</code>
                        </div>
                      </div>
                    </div>

                    {/* Passo 5 */}
                    <div className="flex gap-4 p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                      <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-bold flex-shrink-0">
                        5
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-green-400 mb-1">
                          Copie a senha e continue
                        </h4>
                        <p className="text-sm text-brand-ivory/70">
                          Copie a senha gerada. Ela só será mostrada uma vez! Depois clique em
                          "Continuar" abaixo para inserir suas credenciais.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Botões */}
                <div className="flex gap-3 pt-4 border-t border-brand-gold/10">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowAppleModal(false);
                      setAppleWizardStep('tutorial');
                    }}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setAppleWizardStep('form')}
                    className="flex-1 bg-brand-gold hover:bg-brand-gold/90 text-brand-ink"
                  >
                    Já tenho minha senha
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            ) : (
              /* Form Step */
              <div className="space-y-6">
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                  <div className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-medium text-green-400 mb-1">Pronto para conectar!</h3>
                      <p className="text-sm text-brand-ivory/70">
                        Insira suas credenciais abaixo. Use a senha de app que você acabou de criar.
                      </p>
                    </div>
                  </div>
                </div>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    console.log('>>> FORM SUBMIT - Connecting Apple Calendar with:', appleCredentials);
                    connectAppleMutation.mutate(appleCredentials);
                  }}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-sm font-medium text-brand-ivory/60 mb-2">
                      URL do CalDAV
                    </label>
                    <div className="px-3 py-2 bg-brand-smoke/30 border border-brand-gold/10 rounded-md text-brand-ivory/50 text-sm">
                      {appleCredentials.calendarUrl}
                    </div>
                    <p className="text-xs text-brand-ivory/40 mt-1">
                      Configurado automaticamente para o iCloud
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brand-ivory mb-2">
                      Apple ID (Email)
                    </label>
                    <Input
                      type="email"
                      value={appleCredentials.username}
                      onChange={(e) =>
                        setAppleCredentials({ ...appleCredentials, username: e.target.value })
                      }
                      placeholder="seu.email@icloud.com"
                      required
                      className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                    />
                    <p className="text-xs text-brand-ivory/50 mt-1">
                      O mesmo email que você usa para entrar no iCloud
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-brand-ivory mb-2 flex items-center gap-2">
                      <Key className="w-4 h-4 text-brand-gold" />
                      Senha de App
                    </label>
                    <div className="relative">
                      <Input
                        type={showApplePassword ? 'text' : 'password'}
                        value={appleCredentials.password}
                        onChange={(e) =>
                          setAppleCredentials({ ...appleCredentials, password: e.target.value })
                        }
                        placeholder="xxxx-xxxx-xxxx-xxxx"
                        required
                        className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory font-mono pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowApplePassword(!showApplePassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-ivory/50 hover:text-brand-ivory transition-colors"
                      >
                        {showApplePassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-amber-500/80 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Use a senha de app gerada, NÃO sua senha normal do iCloud
                    </p>
                  </div>

                  <div className="flex gap-3 pt-4 border-t border-brand-gold/10">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setAppleWizardStep('tutorial')}
                      className="flex-1"
                    >
                      <ChevronLeft className="w-4 h-4 mr-2" />
                      Voltar
                    </Button>
                    <Button
                      type="submit"
                      disabled={connectAppleMutation.isPending}
                      className="flex-1 bg-brand-gold hover:bg-brand-gold/90 text-brand-ink"
                    >
                      {connectAppleMutation.isPending ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Conectando...
                        </>
                      ) : (
                        <>
                          <Link2 className="w-4 h-4 mr-2" />
                          Conectar
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
}
